import { Button, InputAdornment } from '@mui/material';
import classNames from 'classnames';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ReactComponent as HidePasswordIcon } from '../../../assets/icons/Login/passwordHideIcon.svg';
import { ReactComponent as ShowPasswordIcon } from '../../../assets/icons/Login/passwordShowIcon.svg';
import TextField from '../../../components/Inputs/TextField';
import { useLoginService } from '../../../services/loginService';
import { useSignUpService } from '../../../services/signUpService';
import { IUserProperties, signUpFlowStatEnum } from '../../../services/signUpService/types';
import formStyles from '../../Login/LoginContent/index.module.scss';

function SignUpForm() {
  const { login } = useLoginService();
  const {
    signUp,
    state,
    state: { errorMessage, signUpMethod },
  } = useSignUpService();
  const isEmailMethod = signUpMethod === 'email';

  const [isShowPassword, setIsShowPassword] = useState<{
    password: boolean;
    confirmPassword: boolean;
  }>({
    password: false,
    confirmPassword: false,
  });
  const {
    register,
    handleSubmit,
    watch,
    formState,
    clearErrors,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const tooglePasswordType = (name: 'password' | 'confirmPassword') => {
    setIsShowPassword({
      ...isShowPassword,
      [name]: !isShowPassword[name],
    });
  };
  const validatePasswords = (confirmPassword: string) => {
    const password = watch('password');
    if (password && password !== confirmPassword) {
      setError('confirmPassword', {
        type: 'custom',
        message: 'Passwords do not match.',
      });
    }
  };

  const onSubmit = (
    formData: Partial<{
      username: string;
      password: string;
      phoneNumber: string;
    }>,
  ) => {
    const { phoneNumber, password } = formData;
    const username = formData?.username?.replace?.(/\s/g, '') ?? '';

    state.setSignUpFlowState({
      state: signUpFlowStatEnum.setUpAccount,
      onSignUp({ name, familyName }: IUserProperties) {
        signUp({
          username,
          password: password ?? '',
          phoneNumber: phoneNumber ?? '',
          extraUserProperties: {
            name,
            familyName,
          },
        });
      },
      onLogin() {
        login({
          username,
          password: password ?? '',
        });
      },
      username,
      password: password ?? '',
    });
  };

  useEffect(() => {
    reset();
  }, [signUpMethod]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col">
        <TextField
          label={signUpMethod}
          wrapperClassName={classNames(formStyles.inputWrapper, {
            [formStyles.password]: !isEmailMethod,
          })}
          error={!!errors?.username}
          helperText={isEmailMethod ? errors?.username?.message : ''}
          inputClassName={formStyles.inputField}
          hideErrorIcon
          variant="outlined"
          InputLabelProps={{
            classes: {
              shrink: formStyles.shrink,
            },
          }}
          {...register('username', {
            required: `Please enter ${signUpMethod}`,
            minLength: !isEmailMethod
              ? { value: 6, message: 'Please Enter a Valid username' }
              : undefined,
            pattern: isEmailMethod
              ? {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Please Enter a Valid Email Address',
                }
              : undefined,
          })}
          onInput={() => clearErrors('username')}
        />
        {!isEmailMethod ? (
          <span
            className={classNames(formStyles.passwordHelperText, {
              [formStyles.error]: !!errors?.username,
            })}
          >
            Username should include at least 6 characters.
          </span>
        ) : null}
      </div>
      <div className="flex flex-col">
        <TextField
          label="Password"
          wrapperClassName={classNames(formStyles.inputWrapper, formStyles.password)}
          error={!!errors?.password}
          inputClassName={formStyles.inputField}
          type={isShowPassword.password ? 'text' : 'password'}
          hideErrorIcon
          InputLabelProps={{
            classes: {
              shrink: formStyles.shrink,
            },
          }}
          variant="outlined"
          {...register('password', {
            required: 'Please enter password',
            pattern: {
              value: /(?=.{8,})/,
              message: 'Password should include at least 8 characters.',
            },
          })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" className={formStyles.endAdornment}>
                <div onClick={() => tooglePasswordType('password')} className="p-0">
                  {isShowPassword.password ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                </div>
              </InputAdornment>
            ),
          }}
          onInput={() => clearErrors('password')}
        />
        <span
          className={classNames(formStyles.passwordHelperText, {
            [formStyles.error]: !!errors?.password,
          })}
        >
          Password should include at least 8 characters.
        </span>
      </div>
      <TextField
        label="Confirm new password"
        wrapperClassName={formStyles.inputWrapper}
        error={!!errors?.confirmPassword}
        helperText={errors?.confirmPassword?.message}
        inputClassName={formStyles.inputField}
        type={isShowPassword.confirmPassword ? 'text' : 'password'}
        hideErrorIcon
        InputLabelProps={{
          classes: {
            shrink: formStyles.shrink,
          },
        }}
        variant="outlined"
        {...register('confirmPassword')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" className={formStyles.endAdornment}>
              <div onClick={() => tooglePasswordType('confirmPassword')} className="p-0">
                {isShowPassword.confirmPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
              </div>
            </InputAdornment>
          ),
        }}
        onBlur={({ target: { value } }) => validatePasswords(value)}
        onInput={() => clearErrors('confirmPassword')}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!(formState.isValid && watch('password') === watch('confirmPassword'))}
        className={clsx(formStyles.submitBtn, 'rounded-4')}
      >
        Continue
      </Button>
      {errorMessage ? <div className={formStyles.error}>{errorMessage}</div> : null}
    </form>
  );
}
export default observer(SignUpForm);
